import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  getMeetingDecisionTypes,
  reorderMeetingDecisionTypes,
  updateMeetingDecisionType,
} from '../api/meetingDecisionTypeApi'
import type {
  MeetingDecisionType,
  MeetingDecisionTypeUpsert,
  PagedResponse,
} from '../types'

const KEY = 'meeting-decision-types'

/**
 * Toplantı Kayıt Tipi sunucu-durumu (042'de <b>liste + güncelle</b>): oluştur/durum/sil/sırala
 * mutation'ları kaldırıldı. Güncellemede güncel kayıt cache'e yazılır (bayat modDate → 409 önlenir).
 */
export function useMeetingDecisionTypes() {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [KEY] })
    // İçerik bölümü lookup'ı (Tutanak Oluştur/Detay/Düzenleme) ayrı sorgu — ad/sıra değişince onu da
    // bayatlat ki tüketen ekranlar refresh olmadan yeni başlık/sırayı alsın.
    qc.invalidateQueries({ queryKey: ['meeting-content-sections'] })
  }

  const applyUpdated = (updated: MeetingDecisionType) =>
    qc.setQueryData<PagedResponse<MeetingDecisionType>>([KEY, i18n.language], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((t) => (t.id === updated.id ? updated : t)) }
        : prev,
    )

  const list = useQuery({
    queryKey: [KEY, i18n.language],
    queryFn: getMeetingDecisionTypes,
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: MeetingDecisionTypeUpsert }) =>
      updateMeetingDecisionType(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    // Domaine özel iş kodu ekranda ele alınır → merkezi hata toast'ı (019) susar.
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  // Yeniden sıralama: liste anında (optimistic) yeni sıraya geçer; backend 1..N yazar. Hata → geri al +
  // global hata toast'ı (tek nokta). onSettled invalidate hem listeyi hem içerik-bölümü lookup'ını tazeler.
  const reorder = useMutation({
    mutationFn: (orderedIds: number[]) => reorderMeetingDecisionTypes(orderedIds),
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: [KEY, i18n.language] })
      const prev = qc.getQueryData<PagedResponse<MeetingDecisionType>>([KEY, i18n.language])
      if (prev) {
        const byId = new Map(prev.content.map((r) => [r.id, r]))
        const content = orderedIds
          .map((id) => byId.get(id))
          .filter((r): r is MeetingDecisionType => r != null)
        qc.setQueryData<PagedResponse<MeetingDecisionType>>([KEY, i18n.language], { ...prev, content })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData([KEY, i18n.language], ctx.prev)
    },
    onSettled: invalidate,
  })

  return { list, update, reorder }
}
